import React, { forwardRef } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils.js";

/* ---------------- Breadcrumb ---------------- */

export interface BreadcrumbItem {
  label: React.ReactNode;
  href?: string;
}

/** chevron mirrors in RTL (a "back" glyph reverses with reading direction);
 *  slash is punctuation and never mirrors. */
export type BreadcrumbSeparator = "chevron" | "slash";

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
  /** Between crumbs. Default "chevron" renders today's ChevronRight. */
  separator?: BreadcrumbSeparator;
  /**
   * Fired when the user activates a crumb. The host navigates — this package
   * stays router-blind (invariant 1), as `SettingsLayout` does with its own
   * `onNavigate`.
   *
   * Absent: the crumb is an ordinary `<a href>` and the browser navigates,
   * which in a single-page host means a full document reload. Present: a
   * plain primary-button click is handed here instead, and every other
   * gesture — middle-click, cmd/ctrl/shift/alt-click — is still left to the
   * browser so "open in a new tab" keeps working.
   */
  onNavigate?: (href: string, item: BreadcrumbItem) => void;
}

const SEPARATORS: Record<BreadcrumbSeparator, () => React.ReactNode> = {
  chevron: () => (
    <ChevronRight className="w-3 h-3 text-pb-border-strong rtl:rotate-180" />
  ),
  slash: () => (
    <span aria-hidden="true" className="text-pb-border-strong">
      /
    </span>
  ),
};

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  function Breadcrumb(
    { items, className, separator = "chevron", onNavigate },
    ref,
  ) {
    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn("flex items-center gap-1.5 text-xs", className)}
      >
        {items.map((it, i) => {
          const last = i === items.length - 1;
          const href = it.href;
          return (
            <React.Fragment key={i}>
              {href && !last ? (
                <a
                  href={href}
                  className="rounded-sm text-pb-text-muted hover:text-pb-text active:text-pb-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/50"
                  onClick={(e) => {
                    if (!onNavigate) return;
                    if (e.defaultPrevented) return;
                    // Anything but a plain primary-button click belongs to the
                    // browser: middle-click and the modifier chords are how a
                    // user opens a crumb in a new tab or window. Enter on a
                    // focused anchor arrives here as a primary-button click,
                    // so keyboard activation follows the same path.
                    if (e.button !== 0) return;
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
                      return;
                    e.preventDefault();
                    onNavigate(href, it);
                  }}
                >
                  {it.label}
                </a>
              ) : (
                <span
                  className={cn(
                    last ? "text-pb-text font-medium" : "text-pb-text-muted",
                  )}
                  aria-current={last ? "page" : undefined}
                >
                  {it.label}
                </span>
              )}
              {!last && SEPARATORS[separator]()}
            </React.Fragment>
          );
        })}
      </nav>
    );
  },
);
